import { Dayjs } from 'dayjs';
import { Location } from '../Api/Trinium/Endpoints/Location/types';
import * as Yup from 'yup';
import { vesselSlice } from '@api/Trinium/Endpoints/Vessels/slice';
import { store } from '@reduxStore';
import { Vessel, Voyage, VoyageRaw } from '@api/Trinium/Endpoints/Vessels/types';
import { apiIsoStringFormat } from '@/Infrastructure/Utilities/dayjs.type';

export interface VesselFormikModalProps {
  onCloseExtension?: () => void;
  apiFixedCacheKey?: string;
}

export interface VesselForm {
  code: string;
  name: string;
}

export interface VesselVoyageFormikModalProps {
  /** Vessel already chosen on the calling form. Seeds the Vessel input and, in edit mode, owns the voyage. */
  vessel?: Vessel | null;
  /** Set when opened from the edit action. Its presence puts the modal in edit mode. */
  voyageId?: number;
  isExport: boolean;
  /** Applies the saved vessel and voyage back onto the form that opened the modal. */
  onSaved?: (vessel: Vessel, voyage: Voyage) => void;
}

export const VesselFormValidationSchema = Yup.object<VesselForm>().shape({
  name: Yup.string().required('Name is required'),
  code: Yup.string().required('Code is required')
});

export interface VesselVoyageForm extends Record<string, unknown> {
  id?: number;
  vessel: Vessel | null;
  terminal: Location | null;
  voyageIn: string;
  etaDate: Dayjs | null;
  demurrageDate: Dayjs | null;
  voyageOut: string;
  frd: Dayjs | null;
  cutoffDate: Dayjs | null;
}

/**
 * A saved voyage is locked down to its dates, so the fields the user can no longer reach are only
 * validated while they are still editable. Otherwise a legacy voyage saved without a terminal could
 * never have its dates corrected.
 */
export function getVesselVoyageValidationSchema(isExport: boolean, isEditMode: boolean) {
  return Yup.object<VesselVoyageForm>().shape({
    vessel: Yup.object().nullable().required('Vessel is required'),
    terminal: isEditMode
      ? Yup.object().nullable().notRequired()
      : Yup.object().nullable().required('Terminal is required'),
    voyageIn: isExport || isEditMode ? Yup.string().notRequired() : Yup.string().required('Voyage In is required'),
    voyageOut: isExport && !isEditMode ? Yup.string().required('Voyage Out is required') : Yup.string().notRequired(),
    /** Dates are the one thing that stays editable forever, so every one of them is type-checked —
     * a half-typed date leaves an invalid Dayjs behind, which would otherwise serialize straight
     * through to the API as the literal text "Invalid Date". */
    etaDate: Yup.date().nullable().typeError('Invalid Date'),
    demurrageDate: Yup.date().nullable().typeError('Invalid Date'),
    frd: Yup.date().nullable().typeError('Invalid Date'),
    cutoffDate: isExport
      ? Yup.date().nullable().typeError('Invalid Date').required('Cut Off Date is required')
      : Yup.date().nullable().notRequired()
  });
}

export function updateVoyageCache(mutatedVoyage: Voyage, vesselId: string) {
  const invalidEndpoints = vesselSlice.util.selectInvalidatedBy(store.getState(), [
    {
      type: 'export-voyages',
      id: vesselId
    },
    {
      type: 'import-voyages',
      id: vesselId
    }
  ]);

  return invalidEndpoints.map((currentInvalidEndpoint) => {
    type VesselEndpoints = typeof vesselSlice.endpoints;

    type InvalidatedEndpointType = VesselEndpoints['fetchVoyages'];

    /** Get the original args as a strongly typed object for the specific endpoint we are handling */
    const originalArgs = currentInvalidEndpoint.originalArgs as InvalidatedEndpointType['Types']['QueryArg'];

    return store.dispatch(
      vesselSlice.util.updateQueryData('fetchVoyages', originalArgs, (draft) => {
        const cacheVoyage = draft.find((voyage) => voyage.id === mutatedVoyage.id);

        if (cacheVoyage) {
          Object.assign(cacheVoyage, {
            ...mutatedVoyage
          });
        } else {
          draft.push(mutatedVoyage);
        }
      })
    );
  });
}

/**
 * Builds the request body for an add or update. Only the fields for the voyage's own direction are
 * sent — the API blanks the opposite direction's values anyway.
 *
 * Dates are sent as an empty string rather than omitted when the user clears one: an update is a
 * PATCH read with "REPLACE", so an absent key keeps whatever is already stored and a date could
 * never be cleared once entered.
 */
export function voyageFormToApiSchema(voyage: VesselVoyageForm, isExport: boolean): Partial<VoyageRaw> {
  const dateOrCleared = (value: Dayjs | null) => (value?.isValid() ? value.format(apiIsoStringFormat) : '');

  if (isExport) {
    return {
      transType: 'E',
      trmId: voyage.terminal?.id ?? '',
      voyageOut: voyage.voyageOut,
      frstRcvDate: dateOrCleared(voyage.frd),
      cutoffDate: dateOrCleared(voyage.cutoffDate)
    };
  }

  return {
    transType: 'I',
    trmId: voyage.terminal?.id ?? '',
    voyageIn: voyage.voyageIn,
    etaDate: dateOrCleared(voyage.etaDate),
    lastFreeDate: dateOrCleared(voyage.demurrageDate)
  };
}
